import React, { useState, useEffect } from "react";
import {
  TableContainer,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  TablePagination,
  CircularProgress,
  Box,
  Typography,
  Paper,
  TextField,
  InputAdornment,
  IconButton,
} from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";
import SearchIcon from "@material-ui/icons/Search";
import ClearIcon from "@material-ui/icons/Clear";
import client from "api/apiAuth/guestClient";

const useStyles = makeStyles((theme) => ({
  root: {
    padding: theme.spacing(3),
    backgroundColor: "#fafafa",
    minHeight: "100vh",
  },
  header: {
    marginBottom: theme.spacing(3),
  },
  title: {
    fontWeight: 600,
    color: theme.palette.primary.main,
    marginBottom: theme.spacing(1),
  },
  subtitle: {
    color: "#666",
    marginBottom: theme.spacing(3),
  },
  tableContainer: {
    backgroundColor: "#fff",
    borderRadius: theme.shape.borderRadius,
    boxShadow: "0px 2px 4px rgba(0, 0, 0, 0.08)",
  },
  tableHead: {
    backgroundColor: theme.palette.primary.main,
  },
  tableHeadCell: {
    color: "#fff",
    fontWeight: 600,
    fontSize: 14,
  },
  tableBodyRow: {
    "&:hover": {
      backgroundColor: "rgba(0, 102, 204, 0.04)",
    },
    "&:nth-of-type(even)": {
      backgroundColor: "#fafafa",
    },
  },
  tableCell: {
    fontSize: 14,
    padding: "16px",
  },
  loadingContainer: {
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    padding: theme.spacing(4),
  },
  emptyContainer: {
    padding: theme.spacing(4),
    textAlign: "center",
  },
  searchContainer: {
    marginBottom: theme.spacing(3),
    display: "flex",
    gap: theme.spacing(1),
  },
  searchField: {
    flex: 1,
    "& .MuiOutlinedInput-root": {
      backgroundColor: "#fff",
      borderRadius: theme.shape.borderRadius,
      transition: "all 0.3s ease",
      "&:hover": {
        boxShadow: "0px 2px 8px rgba(0, 102, 204, 0.1)",
      },
      "&.Mui-focused": {
        boxShadow: "0px 4px 12px rgba(0, 102, 204, 0.2)",
        "& fieldset": {
          borderColor: theme.palette.primary.main,
          borderWidth: 2,
        },
      },
    },
    "& .MuiOutlinedInput-input": {
      fontSize: 14,
      padding: "12px 14px",
      "&::placeholder": {
        color: "#999",
        opacity: 1,
      },
    },
  },
  searchIcon: {
    color: theme.palette.primary.main,
  },
  clearButton: {
    color: "#999",
    "&:hover": {
      color: theme.palette.primary.main,
    },
  },
}));

const AnnualLimitDashboard = () => {
  const classes = useStyles();

  const [loading, setLoading] = useState(false);
  const [doctors, setDoctors] = useState([]);
  const [totalCount, setTotalCount] = useState(0);
  const [searchQuery, setSearchQuery] = useState("");
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  // Debounce timer for search
  const debounceTimerRef = React.useRef(null);

  useEffect(() => {
    fetchDoctorsData(page, rowsPerPage, searchQuery);
  }, [page, rowsPerPage]);

  useEffect(() => {
    // Debounce search queries
    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
    }

    debounceTimerRef.current = setTimeout(() => {
      setPage(0); // Reset to first page when searching
      fetchDoctorsData(0, rowsPerPage, searchQuery);
    }, 500); // 500ms debounce

    return () => {
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }
    };
  }, [searchQuery, rowsPerPage]);

  const fetchDoctorsData = async (pageNum, perPage, search) => {
    try {
      setLoading(true);
      const params = {
        page: pageNum + 1, // Backend usually expects 1-based page numbers
        limit: perPage,
        search: search.trim(),
      };

      const response = await client.get(`doctor/annual-limit-statistics`, {
        params,
      });

      setDoctors(response.data.data || []);
      setTotalCount(response.data.total || 0);
    } catch (error) {
      console.error("Error fetching doctors data:", error);
      setDoctors([]);
      setTotalCount(0);
    } finally {
      setLoading(false);
    }
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleSearchChange = (event) => {
    setSearchQuery(event.target.value);
  };

  const handleClearSearch = () => {
    setSearchQuery("");
  };

  const displayedDoctors = doctors;

  return (
    <Box className={classes.root}>
      <Box className={classes.header}>
        <Typography variant="h4" className={classes.title}>
          Annual Engagment Dashboard
        </Typography>
        <Typography variant="body2" className={classes.subtitle}>
          Monitor doctor annual limits and track current year requests
        </Typography>
      </Box>

      {/* Search Field */}
      <Box className={classes.searchContainer}>
        <TextField
          className={classes.searchField}
          placeholder="Search by doctor name..."
          variant="outlined"
          size="small"
          value={searchQuery}
          onChange={handleSearchChange}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon className={classes.searchIcon} />
              </InputAdornment>
            ),
            endAdornment: searchQuery && (
              <InputAdornment position="end">
                <IconButton
                  size="small"
                  onClick={handleClearSearch}
                  className={classes.clearButton}
                  edge="end"
                >
                  <ClearIcon />
                </IconButton>
              </InputAdornment>
            ),
          }}
        />
      </Box>
      {loading ? (
        <Box className={classes.loadingContainer}>
          <CircularProgress color="primary" size={50} />
        </Box>
      ) : displayedDoctors.length > 0 ? (
        <TableContainer component={Paper} className={classes.tableContainer}>
          <Table>
            <TableHead className={classes.tableHead}>
              <TableRow>
                <TableCell className={classes.tableHeadCell} align="center">
                  #
                </TableCell>
                <TableCell className={classes.tableHeadCell}>
                  Doctor Name
                </TableCell>
                <TableCell className={classes.tableHeadCell} align="center">
                  Number of Doctor Exceptions
                </TableCell>
                <TableCell className={classes.tableHeadCell}>
                  Segmentation
                </TableCell>
                <TableCell className={classes.tableHeadCell} align="center">
                  Segmentation Annual Limit
                </TableCell>
                <TableCell className={classes.tableHeadCell} align="center">
                  Total Available Requests
                </TableCell>
                <TableCell className={classes.tableHeadCell} align="center">
                  Number of Engagment (This Year)
                </TableCell>
                <TableCell className={classes.tableHeadCell} align="center">
                  Remaining Engagment (This Year)
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {displayedDoctors.map((doctor, index) => (
                <TableRow key={doctor.id} className={classes.tableBodyRow}>
                  <TableCell align="center" className={classes.tableCell}>
                    {page * rowsPerPage + index + 1}
                  </TableCell>
                  <TableCell className={classes.tableCell}>
                    {doctor.name}
                  </TableCell>
                  <TableCell className={classes.tableCell} align="center">
                    {doctor.doctorAnnualLimit || 0}
                  </TableCell>
                  <TableCell className={classes.tableCell}>
                    {doctor.segementation || "-"}
                  </TableCell>
                  <TableCell className={classes.tableCell} align="center">
                    {doctor.segementationAnnualLimit || 0}
                  </TableCell>
                  <TableCell className={classes.tableCell} align="center">
                    {doctor.totalAvailableRequests || 0}
                  </TableCell>
                  <TableCell className={classes.tableCell} align="center">
                    {doctor.totalRequestsCurrentYear || 0}
                  </TableCell>
                  <TableCell className={classes.tableCell} align="center">
                    {doctor.remainingRequests || 0}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
          <TablePagination
            rowsPerPageOptions={[5, 10, 25, 50]}
            component="div"
            count={totalCount}
            rowsPerPage={rowsPerPage}
            page={page}
            onPageChange={handleChangePage}
            onRowsPerPageChange={handleChangeRowsPerPage}
            style={{
              borderTop: `1px solid #e0e0e0`,
              backgroundColor: "#fafafa",
            }}
          />
        </TableContainer>
      ) : searchQuery.trim() !== "" ? (
        <Paper className={classes.emptyContainer}>
          <Typography variant="body1" color="textSecondary">
            No doctors found matching "{searchQuery}"
          </Typography>
        </Paper>
      ) : (
        <Paper className={classes.emptyContainer}>
          <Typography variant="body1" color="textSecondary">
            No doctors found
          </Typography>
        </Paper>
      )}
    </Box>
  );
};

export default AnnualLimitDashboard;
